import React from 'react';
import { Form, Link } from 'react-router-dom';
import { type ValidationError } from '@oysterjs/core/graphql';
import { getCaraTriggerEmailAddress } from '@oysterjs/core/email';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Textarea } from '@oysterjs/ui/textarea';
import { MultiListbox, MultiListboxLabel, MultiListboxOption } from '@oysterjs/ui/multiselect';
import { Button } from '@oysterjs/ui/button';
import { ThenStep, WhenStep } from '~/components/triggers/trigger-flow-steps';
import { Description, Field, Label } from '@oysterjs/ui/fieldset';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  TriggerEventType,
  TriggerCallType,
  TriggerCallRouting,
  type CustomPresetFieldsFragment
} from '~/api/graphql';
import { buildDefinitionInput, type TriggerFormState } from '~/pages/workflows/trigger-form';
import { TriggerTaskField, taskUseCase } from '~/pages/workflows/trigger-task-picker';
import {
  DAYS_LABELS,
  ScheduleWeekday,
  CALL_TYPE_LABELS,
  CALL_ROUTING_LABELS,
  normalizeEmailSuffix,
  formatScheduleDays
} from '~/pages/workflows/trigger-utils';
import { timezoneOptionsForValue } from '~/lib/agency-timezones';
import { CreationControls } from '~/components/triggers/email-builder/creation-controls';
import { CreationSectionCard } from '~/components/triggers/email-builder/creation-card';
import { BuilderField, Help } from '~/components/triggers/email-builder/fields';
import {
  CreationAction,
  CreationSourceChoice
} from '~/components/triggers/email-builder/creation-then';
import {
  CreationEventContext,
  CreationEventField,
  useCreationEventGuard,
  useCreationNameError
} from '~/components/triggers/email-builder/creation-event';
import '~/components/triggers/email-builder/creation-editor.css';

const DAYS = [
  ScheduleWeekday.Mon,
  ScheduleWeekday.Tue,
  ScheduleWeekday.Wed,
  ScheduleWeekday.Thu,
  ScheduleWeekday.Fri,
  ScheduleWeekday.Sat,
  ScheduleWeekday.Sun
];

/** Create-only presentation; the existing legacy form owns serialization and POST. */
export const LegacyCreationEditor = ({
  form,
  onFieldChange,
  onEmailSuffixChange,
  onScheduleDaysChange,
  onScheduleTimeChange,
  onSubmit,
  canSave,
  scheduleReady,
  saving,
  validationErrors,
  saveError,
  customPresets,
  emailNamespace,
  emailSuffixInvalid,
  emailSuffixServerError,
  dirty
}: {
  form: TriggerFormState;
  onFieldChange: <K extends keyof TriggerFormState>(key: K, value: TriggerFormState[K]) => void;
  onEmailSuffixChange: (value: string) => void;
  onScheduleDaysChange: (value: ScheduleWeekday[]) => void;
  onScheduleTimeChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  canSave: boolean;
  scheduleReady: boolean;
  saving: boolean;
  validationErrors: ValidationError[];
  saveError: string | null;
  customPresets: CustomPresetFieldsFragment[];
  emailNamespace: string;
  emailSuffixInvalid: boolean;
  emailSuffixServerError?: string | null;
  dirty: boolean;
}) => {
  const creation = React.useContext(CreationEventContext);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [source, setSource] = React.useState<'preset' | 'prompt'>('preset');
  const [sourceRequired, setSourceRequired] = React.useState(false);
  useCreationEventGuard(dirty || source !== 'preset', saving);
  const errors = Object.fromEntries(
    validationErrors.map((error) => [error.field.join('.'), error.message])
  );
  if (emailSuffixInvalid)
    errors['caraAddressConfig.emailSuffix'] =
      'Use lowercase letters and numbers, with single hyphens between words.';
  else if (emailSuffixServerError) errors['caraAddressConfig.emailSuffix'] = emailSuffixServerError;
  useCreationNameError(creation ? errors.name : undefined);
  const sourceReady =
    source === 'prompt' ? form.task === null && !!form.prompt.trim() : form.task !== null;
  if (sourceRequired && !sourceReady)
    errors.target = 'Choose a preset or switch to Prompt explicitly and enter instructions.';
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (!sourceReady) {
      event.preventDefault();
      setSourceRequired(true);
      return;
    }
    onSubmit(event);
  };
  const summaryErrors = validationErrors.filter(
    (error) =>
      error.field.join('.') !== 'caraAddressConfig.emailSuffix' && error.message !== saveError
  );
  const task = form.task;
  const useCase = taskUseCase(task);
  const timezoneOptions = timezoneOptionsForValue(form.scheduleTimezone);
  const suffix = normalizeEmailSuffix(form.emailSuffix);
  const address =
    emailNamespace && suffix && !emailSuffixInvalid && !emailSuffixServerError
      ? getCaraTriggerEmailAddress(emailNamespace, suffix)
      : null;
  // The address around the editable suffix, so the input can sit inside it on one line.
  const emailAddressParts = (() => {
    if (!emailNamespace) return null;
    const [prefix, domain] = getCaraTriggerEmailAddress(emailNamespace, '').split('…');
    return { prefix, domain };
  })();
  // Read-only review must not serialize an incomplete schedule during render.
  const reviewInput = scheduleReady ? buildDefinitionInput(form, null) : null;
  const eventSummary =
    form.eventType === TriggerEventType.EmailReceived
      ? address
        ? `Email forwarded or BCC’d to ${address}, after alias delivery is provisioned.`
        : 'Choose an available Cara email address.'
      : form.eventType === TriggerEventType.ScheduleDue
        ? `Every ${formatScheduleDays(form.scheduleDays) || '(choose days)'} at ${form.scheduleTime || '(choose a time)'} ${form.scheduleTimezone}.`
        : `After a call ends: ${CALL_TYPE_LABELS[form.callType]}, ${CALL_ROUTING_LABELS[form.callRouting]}, at least ${form.callMinimumDurationSeconds} seconds.`;
  const instructionsRequired = useCase?.requires === 'instructions';
  const promptField = (
    <BuilderField
      label={
        source === 'prompt'
          ? 'Prompt'
          : `${useCase?.instructions.label ?? 'Additional instructions'}${instructionsRequired ? '' : ' (optional)'}`
      }
      path="target.prompt"
      errors={errors}
      description={`${
        source === 'prompt'
          ? 'Required. Tell Cara what to do in the new workspace; no preset is used.'
          : instructionsRequired
            ? 'Required for this preset. Added to the preset’s own instructions.'
            : 'Optional. Added to the preset’s own instructions.'
      }${form.eventType === TriggerEventType.EmailReceived ? ' The incoming email is included automatically.' : ''}`}
    >
      {(props) => (
        <Textarea
          {...props}
          rows={4}
          value={form.prompt}
          onChange={(event) => onFieldChange('prompt', event.target.value)}
          placeholder={
            source === 'prompt'
              ? 'What should Cara do?'
              : (useCase?.instructions.placeholder ?? 'Anything else Cara should know?')
          }
        />
      )}
    </BuilderField>
  );

  return (
    <CreationControls.Provider value>
      <Form className="space-y-6" aria-label="Create trigger" onSubmit={handleSubmit}>
        {!creation && (
          <div className="rounded-lg bg-white shadow dark:bg-neutral-800">
            <div className="px-4 py-5 sm:px-6">
              <BuilderField
                label="Trigger name"
                path="name"
                errors={errors}
                description="A short label shown in the triggers list. Starts monitoring on creation."
              >
                {(props) => (
                  <Input
                    {...props}
                    className="max-w-md"
                    value={form.name}
                    disabled={saving}
                    onChange={(event) => onFieldChange('name', event.target.value)}
                  />
                )}
              </BuilderField>
            </div>
          </div>
        )}
        {(saveError || summaryErrors.length > 0) && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400">
            {saveError && <p>{saveError}</p>}
            {summaryErrors.length > 0 && (
              <ul>
                {summaryErrors.map((error, index) => (
                  <li key={`${error.field.join('.')}-${index}`}>{error.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <fieldset
          className="email-creation-flow flex flex-col gap-6 disabled:opacity-75"
          disabled={saving}
        >
          <CreationSectionCard step="when">
            <div className="space-y-4">
              <CreationEventField />
              {form.eventType === TriggerEventType.EmailReceived && (
                <div className="max-w-xl">
                  <BuilderField
                    label="Email address"
                    path="caraAddressConfig.emailSuffix"
                    errors={errors}
                    description="Forward or BCC to this address after alias delivery is provisioned."
                  >
                    {(props) =>
                      emailAddressParts ? (
                        <div
                          data-slot="control"
                          className="flex items-center gap-1 text-sm/6 text-neutral-600 dark:text-neutral-400"
                        >
                          <span className="shrink-0 font-mono">{emailAddressParts.prefix}</span>
                          <Input
                            {...props}
                            className="min-w-24 flex-1"
                            value={form.emailSuffix}
                            placeholder="intake"
                            onChange={(event) => onEmailSuffixChange(event.target.value)}
                          />
                          <span className="shrink-0 font-mono">{emailAddressParts.domain}</span>
                        </div>
                      ) : (
                        <Input
                          {...props}
                          value={form.emailSuffix}
                          placeholder="intake"
                          onChange={(event) => onEmailSuffixChange(event.target.value)}
                        />
                      )
                    }
                  </BuilderField>
                </div>
              )}
              {form.eventType === TriggerEventType.EmailReceived && !emailNamespace && (
                <Help className="text-amber-700 dark:text-amber-300">
                  Claim an agency email handle in <Link to="/app/settings/agency">settings</Link>{' '}
                  before creating a Cara address trigger.
                </Help>
              )}
              {form.eventType === TriggerEventType.ScheduleDue && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <BuilderField label="Days of the week" path="scheduleConfig.cron" errors={errors}>
                    {(props) => (
                      <MultiListbox
                        {...props}
                        value={form.scheduleDays}
                        onChange={onScheduleDaysChange}
                        placeholder="Select days..."
                      >
                        {DAYS.map((day) => (
                          <MultiListboxOption key={day} value={day}>
                            <MultiListboxLabel>{DAYS_LABELS[day]}</MultiListboxLabel>
                          </MultiListboxOption>
                        ))}
                      </MultiListbox>
                    )}
                  </BuilderField>
                  <BuilderField label="Time" path="scheduleTime" errors={errors}>
                    {(props) => (
                      <Input
                        {...props}
                        type="time"
                        value={form.scheduleTime}
                        onChange={(event) => onScheduleTimeChange(event.target.value)}
                      />
                    )}
                  </BuilderField>
                  <BuilderField label="Time zone" path="scheduleConfig.timezone" errors={errors}>
                    {(props) => (
                      <Select
                        {...props}
                        value={form.scheduleTimezone}
                        onChange={(event) => onFieldChange('scheduleTimezone', event.target.value)}
                      >
                        {timezoneOptions.map((zone) => (
                          <option key={zone.value} value={zone.value}>
                            {zone.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </BuilderField>
                  <Help>{eventSummary}</Help>
                  {!scheduleReady && (
                    <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                      Choose at least one day and enter a valid time.
                    </p>
                  )}
                </div>
              )}
              {form.eventType === TriggerEventType.CallAnalyzed && (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <BuilderField
                    label="Trigger on"
                    path="callAnalyzedConfig.callType"
                    errors={errors}
                  >
                    {(props) => (
                      <Select
                        {...props}
                        value={form.callType}
                        onChange={(event) =>
                          onFieldChange('callType', event.target.value as TriggerCallType)
                        }
                      >
                        {Object.values(TriggerCallType).map((type) => (
                          <option key={type} value={type}>
                            {CALL_TYPE_LABELS[type]}
                          </option>
                        ))}
                      </Select>
                    )}
                  </BuilderField>
                  <BuilderField
                    label="Customer type"
                    path="callAnalyzedConfig.routing"
                    errors={errors}
                  >
                    {(props) => (
                      <Select
                        {...props}
                        value={form.callRouting}
                        onChange={(event) =>
                          onFieldChange('callRouting', event.target.value as TriggerCallRouting)
                        }
                      >
                        {Object.values(TriggerCallRouting).map((routing) => (
                          <option key={routing} value={routing}>
                            {CALL_ROUTING_LABELS[routing]}
                          </option>
                        ))}
                      </Select>
                    )}
                  </BuilderField>
                  <BuilderField
                    label="Minimum call length (minutes)"
                    path="callAnalyzedConfig.minimumDurationSeconds"
                    errors={errors}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={0}
                        value={String(Math.floor(form.callMinimumDurationSeconds / 60))}
                        onChange={(event) =>
                          onFieldChange(
                            'callMinimumDurationSeconds',
                            (Number(event.target.value) || 0) * 60
                          )
                        }
                      />
                    )}
                  </BuilderField>
                </div>
              )}
            </div>
          </CreationSectionCard>
          <CreationSectionCard step="then">
            <CreationAction
              issues={
                validationErrors.filter((error) => error.field[0] === 'target').length +
                Number(sourceRequired && !sourceReady)
              }
            >
              <CreationSourceChoice
                value={source}
                disabled={saving}
                onChange={(next) => {
                  setSource(next);
                  if (next === 'prompt') onFieldChange('task', null);
                }}
              />
              {source === 'preset' && (
                <Field>
                  <Label>Preset</Label>
                  <Description>Choose a built-in preset or one your agency made.</Description>
                  <TriggerTaskField
                    value={form.task}
                    customPresets={customPresets}
                    onChange={(next) => onFieldChange('task', next)}
                    disabled={saving}
                  />
                </Field>
              )}
              <div className="min-w-0">{promptField}</div>
              {errors.target && <p role="alert">{errors.target}</p>}
              <Help>This event uses the default workspace access settings.</Help>
            </CreationAction>
          </CreationSectionCard>
        </fieldset>
        <div className="flex justify-end gap-3">
          <Button color="white" type="button" onClick={() => setReviewOpen(true)}>
            Review trigger
          </Button>
          <Button color="white" href="/app/workflows/triggers">
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            loading={saving}
            disabled={!canSave || saving || !sourceReady}
          >
            {saving ? 'Saving…' : 'Create trigger'}
          </Button>
        </div>
      </Form>
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} size="xl">
        <DialogTitle>Review your trigger</DialogTitle>
        <DialogDescription>
          Check the settings below. Nothing is saved or evaluated here.
        </DialogDescription>
        <DialogBody>
          {reviewInput ? (
            <ol className="m-0 list-none rounded-lg border border-neutral-200 p-4 dark:border-neutral-700">
              <WhenStep matcher={reviewInput.eventMatcher} emailNamespace={emailNamespace} />
              <ThenStep target={reviewInput.target} />
            </ol>
          ) : (
            <p role="alert">Choose at least one day and enter a valid time.</p>
          )}
        </DialogBody>
        <DialogActions>
          <Button color="white" type="button" onClick={() => setReviewOpen(false)}>
            Back to editor
          </Button>
        </DialogActions>
      </Dialog>
    </CreationControls.Provider>
  );
};
